/**
 * Fischer Hockey PDP Natural Section Snap Scrolling Engine
 * File: assets/fischer-pdp-scroll-snap.js
 * 
 * Provides natural bidirectional scroll-snapping between:
 * - Fold 1: Product Information (Media Gallery, Buy Box & Description)
 * - Fold 2: Engineering Showcase (#fischer-product-engineering)
 * 
 * Automatically snaps down to Fold 2 when scrolling past Fold 1.
 * Automatically snaps back up to top of PDP (0) when scrolling up from Fold 2.
 * Freely allows standard document scrolling below Fold 2 into recommendations and footer.
 * Synchronizes the Product Overview / Engineering Showcase tab navigation.
 */

(function initFischerPdpScrollSnap() {
  if (typeof window === 'undefined') return;

  function setup() {
    const mainSec = document.querySelector('.product-information');
    const engSec = document.querySelector('#fischer-product-engineering');
    if (!mainSec || !engSec) return;

    const tabOverview = document.getElementById('fischer-tab-overview');
    const tabEngineering = document.getElementById('fischer-tab-engineering');

    let isSnapping = false;
    let scrollTimeout = null;
    let lastScrollY = window.scrollY;
    let scrollDirection = 'down';

    function getEngineeringTargetY() {
      const rect = engSec.getBoundingClientRect();
      return Math.max(0, Math.round(rect.top + window.scrollY));
    }

    function updateTabs(currentY, targetY) {
      const threshold = targetY * 0.55;
      if (currentY >= threshold) {
        if (tabOverview) {
          tabOverview.classList.remove('is-active');
          tabOverview.setAttribute('aria-selected', 'false');
        }
        if (tabEngineering) {
          tabEngineering.classList.add('is-active');
          tabEngineering.setAttribute('aria-selected', 'true');
        }
      } else {
        if (tabOverview) {
          tabOverview.classList.add('is-active');
          tabOverview.setAttribute('aria-selected', 'true');
        }
        if (tabEngineering) {
          tabEngineering.classList.remove('is-active');
          tabEngineering.setAttribute('aria-selected', 'false');
        }
      }
    }

    function smoothScrollTo(targetY) {
      if (Math.abs(window.scrollY - targetY) < 5) return;
      isSnapping = true;
      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });
      setTimeout(() => {
        isSnapping = false;
        lastScrollY = window.scrollY;
      }, 700);
    }

    // Tab Click: Smooth scroll directly to engineering fold
    if (tabEngineering) {
      tabEngineering.addEventListener('click', (e) => {
        e.preventDefault();
        const targetY = getEngineeringTargetY();
        smoothScrollTo(targetY);
      });
    }

    // Tab Click: Smooth scroll directly to top product overview fold
    if (tabOverview) {
      tabOverview.addEventListener('click', (e) => {
        e.preventDefault();
        smoothScrollTo(0);
      });
    }

    // Scroll listener: Track direction & evaluate snap points on settling
    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      scrollDirection = currentY > lastScrollY ? 'down' : 'up';
      lastScrollY = currentY;

      const targetY = getEngineeringTargetY();
      updateTabs(currentY, targetY);

      if (isSnapping) return;

      // Only perform automatic snap on desktop screens
      if (window.innerWidth < 750) return;

      // If user is scrolled well past Fold 2 (into recommendations or footer), allow free scroll
      if (currentY > targetY + 140) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (isSnapping) return;
        const settledY = window.scrollY;
        const currentTargetY = getEngineeringTargetY();

        // 1. User scrolled down from Fold 1 past initial reading zone -> snap to Fold 2
        if (scrollDirection === 'down' && settledY > 140 && settledY < currentTargetY - 40) {
          smoothScrollTo(currentTargetY);
        }
        // 2. User scrolled up from Fold 2 into transition zone -> snap back to top of PDP
        else if (scrollDirection === 'up' && settledY < currentTargetY - 40 && settledY > 20) {
          smoothScrollTo(0);
        }
      }, 180);
    }, { passive: true });

    // Enable CSS smooth scrolling on html
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
